import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useSocketEmitter, useSocketReceiver } from '@/hooks/use-sockets';
import { useAuth } from '@/hooks/use-auth';

const ChatRequestContext = createContext();

export const ChatRequestProvider = ({ children }) => {
    const [chatRequests, setChatRequests] = useState([]);
    const [activeRequest, setActiveRequest] = useState(null);
    const [showNotification, setShowNotification] = useState(false);
    const [userSocketId, setUserSocketIt] = useState(null);
    const { astrologer } = useAuth();
    const { emitter } = useSocketEmitter();
    const audioRef = useRef(null);

    // Initialize audio element for notification sound
    useEffect(() => {
        audioRef.current = new Audio('/notification.mp3');
    }, []);
    // Listen for incoming chat requests
    useSocketReceiver('chat-request', (requestData) => {
        // Only handle requests directed to this astrologer
        if (requestData.astrologerId === astrologer?.astrologerId) {
            console.log('New chat request received:', requestData);
            setUserSocketIt(requestData.userSocketId)
            // Check if the request is already in the list
            // Play notification sound
            try {
                audioRef.current?.play();
            } catch (error) {
                console.error("Error playing notification sound:", error);
            }

            // Add to chat requests with expiration time (2 minutes)
            const newRequest = {
                ...requestData,
                receivedAt: new Date(),
                expiresAt: new Date(Date.now() + 2 * 60 * 1000) // 2 minutes
            };

            setChatRequests(prev => [...prev, newRequest]);
            setActiveRequest(newRequest);
            setShowNotification(true);
        }
    });

    // Clean up expired requests periodically
    useEffect(() => {
        const interval = setInterval(() => {
            const now = new Date();
            setChatRequests(requests => {
                const updatedRequests = requests.filter(req => new Date(req.expiresAt) > now);
                // If active request expired, hide notification
                if (activeRequest && new Date(activeRequest.expiresAt) <= now) {
                    setActiveRequest(null);
                    setShowNotification(false);
                }
                return updatedRequests;
            });
        }, 10000); // Check every 10 seconds

        return () => clearInterval(interval);
    }, [activeRequest]);

    // Function to accept a chat request
    const acceptChatRequest = (chatId) => {
        const request = chatRequests.find(req => req.chatId === chatId);
        if (!request) return;

        // Send socket acceptance
        emitter("chat-response", {
            userSocketId,
            status: true,
            message: "Chat request accepted",
            chatId: chatId
        });

        // Remove request from list and close notification
        setChatRequests(prev => prev.filter(req => req.chatId !== chatId));
        setShowNotification(false);
        setActiveRequest(null);

        // Extract userId from chatId (format: chat_userId_astrologerId)
        const userId = chatId.split('_')[1];

        // Navigate to chat with this user
        if (userId) {
            window.location.href = `/interact/chat/${userId}`; // Update with your chat page URL
        }
    };

    // Function to decline a chat request
    const declineChatRequest = (chatId, reason = "Astrologer unavailable") => {
        // Send socket decline
        emitter("chat-response", {
            userSocketId: userSocketId,
            status: false,
            message: reason,
            chatId: chatId
        });

        // Remove from requests and close notification
        setChatRequests(prev => prev.filter(req => req.chatId !== chatId));

        // If this was the active request being shown, clear it
        if (activeRequest?.chatId === chatId) {
            setShowNotification(false);
            setActiveRequest(null);
        }
    };

    const closeNotification = () => {
        setShowNotification(false);
    };

    const value = {
        chatRequests,
        activeRequest,
        showNotification,
        acceptChatRequest,
        declineChatRequest,
        closeNotification
    };

    return (
        <ChatRequestContext.Provider value={value}>
            {children}
        </ChatRequestContext.Provider>
    );
};

export const useChatRequests = () => {
    const context = useContext(ChatRequestContext);
    if (context === undefined) {
        throw new Error('useChatRequests must be used within a ChatRequestProvider');
    }
    return context;
};